import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Breadcrumb, Button, Collapse, Spin } from "antd";
import { DoubleLeftOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { Link, useLocation, useParams } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import KycService from "../../services/kyc-service";
import {
	buildKycCompanyTabUrl,
	buildKycMaterialDesignTabReturn,
	buildKycMaterialsTableSearchReturn,
	buildKycTableSearchUrl,
	loadKycDesignNavContext,
	saveKycDesignNavContext,
} from "./kycHelpers";
import {
	DesignSectionFieldGrid,
	KYC_DEFAULT_FILTER_PAYLOAD,
	parseDesignDetailsListResponse,
} from "./kycDesignShared";
import "./KYC-Dashboard.css";
import { KYCOrdersImage, KYCQuotationImage } from "../Elite/imageAssets";

const { Panel } = Collapse;

const KycDesignDetails = () => {
	const { generalDetailId } = useParams();
	const location = useLocation();
	const [isLoading, setIsLoading] = useState(false);
	const [sectionList, setSectionList] = useState([]);
	const [activePanels, setActivePanels] = useState([]);

	const resolvedGeneralDetailId = useMemo(() => {
		const fromParam = generalDetailId ? String(generalDetailId).trim() : "";
		if (fromParam) {
			return fromParam;
		}
		return location.state?.generalDetailId ? String(location.state.generalDetailId).trim() : "";
	}, [generalDetailId, location.state?.generalDetailId]);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [resolvedGeneralDetailId]);

	const navContext = useMemo(() => {
		const fromState = location.state || {};
		const stored = loadKycDesignNavContext(resolvedGeneralDetailId);
		return {
			designMasterNumber:
				fromState.designMasterNumber || stored.designMasterNumber || resolvedGeneralDetailId || "Design",
			groupCompanyId: fromState.groupCompanyId || stored.groupCompanyId || "",
			groupCompanyName: fromState.groupCompanyName || stored.groupCompanyName || "",
			customerName: fromState.customerName || stored.customerName || "",
			material: fromState.material || stored.material || "",
			returnSource: fromState.returnSource || stored.returnSource || "dashboard",
			materialProductName: fromState.materialProductName || stored.materialProductName || "",
			materialName: fromState.materialName || stored.materialName || "",
			materialsCustomer: fromState.materialsCustomer || stored.materialsCustomer || "",
			accountName: fromState.accountName || stored.accountName || "",
			returnTab: fromState.returnTab || stored.returnTab || "design",
		};
	}, [location.state, resolvedGeneralDetailId]);

	const isMaterialReturn =
		navContext.returnSource === "material" && Boolean(navContext.materialProductName);

	useEffect(() => {
		if (!resolvedGeneralDetailId) {
			return;
		}

		saveKycDesignNavContext(resolvedGeneralDetailId, {
			designMasterNumber: navContext.designMasterNumber,
			groupCompanyId: navContext.groupCompanyId,
			groupCompanyName: navContext.groupCompanyName,
			customerName: navContext.customerName,
			material: navContext.material,
			returnSource: navContext.returnSource,
			materialProductName: navContext.materialProductName,
			materialName: navContext.materialName,
			materialsCustomer: navContext.materialsCustomer,
			accountName: navContext.accountName,
			returnTab: navContext.returnTab,
		});
	}, [resolvedGeneralDetailId, navContext]);

	const dashboardDesignTabLink = buildKycCompanyTabUrl("design");

	useEffect(() => {
		if (!resolvedGeneralDetailId) {
			return undefined;
		}

		setIsLoading(true);
		const requestData = {
			pageindex: 1,
			pagesize: 10,
			sortcolumn: "",
			orderBy: "",
			search: "",
			...KYC_DEFAULT_FILTER_PAYLOAD,
			generalDetailId: Number(resolvedGeneralDetailId) || resolvedGeneralDetailId,
		};

		KycService.getKycDesignDetailsListAPI(requestData)
			.then((response) => {
				const success = response.data?.statusCode === 200 || response.data?.status === true;
				if (success) {
					const sections = parseDesignDetailsListResponse(response);
					setSectionList(sections);
					setActivePanels(sections.map((section) => section.groupKey));
				} else {
					NotificationManager.error(response.data?.message || "Unable to load design details.");
					setSectionList([]);
					setActivePanels([]);
				}
			})
			.catch((error) => {
				console.log(error, "Error");
				NotificationManager.error("Unable to load design details.");
				setSectionList([]);
				setActivePanels([]);
			})
			.finally(() => {
				setIsLoading(false);
			});

		return undefined;
	}, [resolvedGeneralDetailId]);

	const handleCollapseChange = (keys) => {
		setActivePanels(Array.isArray(keys) ? keys : keys ? [keys] : []);
	};

	const designBreadcrumbLabel = useMemo(() => {
		return navContext.designMasterNumber || "Design";
	}, [navContext.designMasterNumber, sectionList]);

	const materialDesignTabReturn = useMemo(
		() =>
			buildKycMaterialDesignTabReturn({
				productName: navContext.materialProductName,
				materialName: navContext.materialName || navContext.materialProductName,
				groupCompanyId: navContext.groupCompanyId,
				groupCompanyName: navContext.groupCompanyName,
				materialsCustomer: navContext.materialsCustomer,
				accountName: navContext.accountName,
				returnTab: navContext.returnTab,
			}),
		[navContext]
	);

	const dashboardMaterialsReturn = useMemo(
		() =>
			buildKycMaterialsTableSearchReturn({
				materialName: navContext.materialName || navContext.materialProductName,
				groupCompanyId: navContext.groupCompanyId,
				groupCompanyName: navContext.groupCompanyName,
				materialsCustomer: navContext.materialsCustomer,
				accountName: navContext.accountName,
			}),
		[navContext]
	);

	const dashboardDesignBackLink = useMemo(() => {
		if (isMaterialReturn) {
			return materialDesignTabReturn.link;
		}
		const search = designBreadcrumbLabel !== "Design" ? String(designBreadcrumbLabel).trim() : "";
		if (search) {
			return buildKycTableSearchUrl(search, { companyTab: "design" });
		}
		return buildKycCompanyTabUrl("design");
	}, [isMaterialReturn, materialDesignTabReturn.link, designBreadcrumbLabel]);

	const dashboardDesignBackState = useMemo(() => {
		if (isMaterialReturn) {
			return materialDesignTabReturn.state;
		}
		const search = designBreadcrumbLabel !== "Design" ? String(designBreadcrumbLabel).trim() : "";
		if (!search) {
			return { returnTab: "design" };
		}
		return {
			returnTab: "design",
			designMasterNumber: search,
			tableSearch: search,
		};
	}, [isMaterialReturn, materialDesignTabReturn.state, designBreadcrumbLabel]);

	const materialBreadcrumbLabel =
		navContext.materialName || navContext.materialProductName || navContext.material || "Material";

	return (
		<Fragment>
			<div className="dashboard app-content content kyc-design-details-page">
				<div className="content-wrapper">
					<div className="content-body">
						<div className="d-flex justify-content-between align-items-center details_view_title kyc-design-details-topbar">
							<Breadcrumb className="details_breadcrum" separator=">">
								<Breadcrumb.Item>
									<Link to="/kyc">Dashboard</Link>
								</Breadcrumb.Item>
								{isMaterialReturn ? (
									<>
										<Breadcrumb.Item>
											<Link
												to={dashboardMaterialsReturn.link}
												state={dashboardMaterialsReturn.state}>
												<label>{materialBreadcrumbLabel}</label>
											</Link>
										</Breadcrumb.Item>
										<Breadcrumb.Item>
											<Link to={dashboardDesignBackLink} state={dashboardDesignBackState}>
												<label>Design</label>
											</Link>
										</Breadcrumb.Item>
										<Breadcrumb.Item>
											<label>{designBreadcrumbLabel}</label>
										</Breadcrumb.Item>
									</>
								) : (
									<>
										<Breadcrumb.Item>
											<Link to={dashboardDesignTabLink}>
												<label>Design</label>
											</Link>
										</Breadcrumb.Item>
										<Breadcrumb.Item>
											<label>{designBreadcrumbLabel}</label>
										</Breadcrumb.Item>
									</>
								)}
							</Breadcrumb>
							<Link to={dashboardDesignBackLink} state={dashboardDesignBackState}>
								<Button type="primary" className="back_btn" icon={<DoubleLeftOutlined />}>
									Back
								</Button>
							</Link>
						</div>

						<h4 className="kyc-design-details-page-title">View Design Details</h4>

						<Spin spinning={isLoading}>
							<div className="row">
								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_blue kyc-design-detail-tile">
										<div className="card-content">
											<div className="kyc-card-body KYCQuotation images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCQuotationImage} alt="Img" />
														<span>- Quotation</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">-</h5>
														<h6 className="text-white mb-0">Won</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3" />
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">-</h5>
														<h6 className="text-white mb-0">Activate</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3" />
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">-</h5>
														<h6 className="text-white mb-0">Lost</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_purple kyc-design-detail-tile">
										<div className="card-content">
											<div className="kyc-card-body KYCOrders images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCOrdersImage} alt="Img" />
														<span>- Orders</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">-</h5>
														<h6 className="text-white mb-0">Approved</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3" />
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">-</h5>
														<h6 className="text-white mb-0">Under Approval</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3" />
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">-</h5>
														<h6 className="text-white mb-0">Rejected</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div className="kyc-design-accordions-wrap">
								{sectionList.length === 0 && !isLoading ? (
									<div className="kyc-tab-placeholder">No design details found.</div>
								) : (
									<Collapse
										activeKey={activePanels}
										onChange={handleCollapseChange}
										className="kyc-mc-collapse kyc-design-sections-collapse">
										{sectionList.map((section) => {
											const isPanelOpen = activePanels.includes(section.groupKey);
											return (
												<Panel
													showArrow={false}
													key={section.groupKey}
													header={
														<span className="kyc-design-panel-header">
															<span className="kyc-design-panel-title">{section.sectionName}</span>
															<span
																className={`kyc-design-expand-icon ${
																	isPanelOpen ? "kyc-design-expand-icon--active" : ""
																}`}>
																{isPanelOpen ? <MinusOutlined /> : <PlusOutlined />}
															</span>
														</span>
													}>
													<h6 className="kyc-design-accordion-section-title">{section.sectionName}</h6>
													<DesignSectionFieldGrid sectionData={section.sectionData} />
												</Panel>
											);
										})}
									</Collapse>
								)}
							</div>
						</Spin>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

export default KycDesignDetails;
